"use client";

import React from "react";
import { ArrowUpRight, BookOpen, FileText, ChartBar as BarChart3, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ResourceItem {
  id: string;
  format: string;
  title: string;
  abstract: string;
  pagesOrReadTime: string;
  quarter: string;
  to: string;
  iconName: "chart" | "book" | "file" | "shield";
}
const resourcesData: ResourceItem[] = [{
  id: "res-1",
  format: "Annual Benchmark",
  title: "2025 B2B Revenue Operations & Data Governance Index",
  abstract: "A cross-market audit of 420 enterprise operations teams evaluating data integrity, tech consolidation, and measurable pipeline velocity.",
  pagesOrReadTime: "48 pages",
  quarter: "Q1 2025",
  to: "/ResearchReportsPage",
  iconName: "chart"
}, {
  id: "res-2",
  format: "Strategic Whitepaper",
  title: "Enterprise AI Adoption: Risk Mitigation & Oversight Frameworks",
  abstract: "Practical governance protocols and decision matrices for implementing foundational language models across regulated workflows.",
  pagesOrReadTime: "34 pages",
  quarter: "Q1 2025",
  to: "/ResearchReportsPage",
  iconName: "shield"
}, {
  id: "res-3",
  format: "Executive Guide",
  title: "The Chief Marketing Officer's Playbook for Full-Funnel Attribution",
  abstract: "Methodologies for unifying multi-touch lead telemetry without relying on third-party tracking cookies or speculative scoring models.",
  pagesOrReadTime: "26 pages",
  quarter: "Archive Edition",
  to: "/EbooksGuidesPage",
  iconName: "book"
}, {
  id: "res-4",
  format: "Empirical Case Study",
  title: "Modernizing Legacy Media Infrastructure: A 3-Year Retrospective",
  abstract: "How a global publishing group reduced technical overhead by 38% while scaling subscriber retention and editorial throughput.",
  pagesOrReadTime: "18 pages",
  quarter: "Special Report",
  to: "/CaseStudiesPage",
  iconName: "file"
}];
export default function Resources() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (type: ResourceItem["iconName"]) => {
    switch (type) {
      case "chart":
        return <BarChart3 className="size-4 text-foreground/80" />;
      case "shield":
        return <ShieldCheck className="size-4 text-foreground/80" />;
      case "book":
        return <BookOpen className="size-4 text-foreground/80" />;
      case "file":
      default:
        return <FileText className="size-4 text-foreground/80" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="popular-resources" data-nav="light" className="w-full bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead Rule & Section Header */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center justify-between gap-4 mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground font-mono">
              Curated Index
            </span>
            <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <span>Peer-reviewed</span>
              <span>•</span>
              <span>Direct access</span>
            </div>
          </div>

          <div className="h-[2px] bg-foreground w-full mb-1" />
          <div className="h-[0.5px] bg-border w-full mb-6" />

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-tight text-foreground">
                Most-read industry publications
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Explore peer-reviewed benchmarks, strategic guides, and empirical case studies built for senior leadership.
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" className="border-foreground/20 hover:bg-muted font-sans text-sm h-10 px-5">
                <Link to="/ResourcesPage" className="inline-flex items-center gap-2">
                  <span>Browse full library</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Resource Cards Asymmetric Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {/* Card 1: Featured Benchmark (Span 7 cols) */}
          <motion.div variants={itemVariants} className="md:col-span-7" data-index={0}>
            <Card className="h-full bg-card border border-border rounded-md hover:border-foreground/40 transition-[border-color,box-shadow] duration-200 shadow-sm flex flex-col justify-between p-6 sm:p-8">
              <div>
                <CardHeader className="p-0 mb-6 flex flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-sm border-border bg-muted/60 text-foreground font-mono text-xs py-0.5 px-2.5">
                      {resourcesData[0].format}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {resourcesData[0].quarter}
                    </span>
                  </div>
                  <div className="p-2 rounded-sm bg-muted/40 border border-border/60">
                    {getIcon(resourcesData[0].iconName)}
                  </div>
                </CardHeader>

                <CardTitle className="p-0 text-2xl sm:text-3xl font-serif font-semibold leading-snug mb-4 text-foreground">
                  <Link to={resourcesData[0].to} className="hover:underline underline-offset-4 decoration-border">
                    {resourcesData[0].title}
                  </Link>
                </CardTitle>

                <CardContent className="p-0">
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {resourcesData[0].abstract}
                  </p>
                </CardContent>
              </div>

              <CardFooter className="p-0 pt-4 border-t border-border/80 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>{resourcesData[0].pagesOrReadTime}</span>
                <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-foreground font-sans font-medium hover:bg-muted/80">
                  <Link to={resourcesData[0].to} className="inline-flex items-center gap-1.5">
                    <span>Read executive brief</span>
                    <ArrowUpRight className="size-3.5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Card 2: Strategic Whitepaper (Span 5 cols) */}
          <motion.div variants={itemVariants} className="md:col-span-5" data-index={1}>
            <Card className="h-full bg-card border border-border rounded-md hover:border-foreground/40 transition-[border-color,box-shadow] duration-200 shadow-sm flex flex-col justify-between p-6 sm:p-8">
              <div>
                <CardHeader className="p-0 mb-6 flex flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-sm border-border bg-muted/60 text-foreground font-mono text-xs py-0.5 px-2.5">
                      {resourcesData[1].format}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {resourcesData[1].quarter}
                    </span>
                  </div>
                  <div className="p-2 rounded-sm bg-muted/40 border border-border/60">
                    {getIcon(resourcesData[1].iconName)}
                  </div>
                </CardHeader>

                <CardTitle className="p-0 text-xl sm:text-2xl font-serif font-semibold leading-snug mb-4 text-foreground">
                  <Link to={resourcesData[1].to} className="hover:underline underline-offset-4 decoration-border">
                    {resourcesData[1].title}
                  </Link>
                </CardTitle>

                <CardContent className="p-0">
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {resourcesData[1].abstract}
                  </p>
                </CardContent>
              </div>

              <CardFooter className="p-0 pt-4 border-t border-border/80 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>{resourcesData[1].pagesOrReadTime}</span>
                <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-foreground font-sans font-medium hover:bg-muted/80">
                  <Link to={resourcesData[1].to} className="inline-flex items-center gap-1.5">
                    <span>Read paper</span>
                    <ArrowUpRight className="size-3.5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Card 3: Executive Guide (Span 5 cols) */}
          <motion.div variants={itemVariants} className="md:col-span-5" data-index={2}>
            <Card className="h-full bg-card border border-border rounded-md hover:border-foreground/40 transition-[border-color,box-shadow] duration-200 shadow-sm flex flex-col justify-between p-6 sm:p-8">
              <div>
                <CardHeader className="p-0 mb-6 flex flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-sm border-border bg-muted/60 text-foreground font-mono text-xs py-0.5 px-2.5">
                      {resourcesData[2].format}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {resourcesData[2].quarter}
                    </span>
                  </div>
                  <div className="p-2 rounded-sm bg-muted/40 border border-border/60">
                    {getIcon(resourcesData[2].iconName)}
                  </div>
                </CardHeader>

                <CardTitle className="p-0 text-xl sm:text-2xl font-serif font-semibold leading-snug mb-4 text-foreground">
                  <Link to={resourcesData[2].to} className="hover:underline underline-offset-4 decoration-border">
                    {resourcesData[2].title}
                  </Link>
                </CardTitle>

                <CardContent className="p-0">
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {resourcesData[2].abstract}
                  </p>
                </CardContent>
              </div>

              <CardFooter className="p-0 pt-4 border-t border-border/80 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>{resourcesData[2].pagesOrReadTime}</span>
                <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-foreground font-sans font-medium hover:bg-muted/80">
                  <Link to={resourcesData[2].to} className="inline-flex items-center gap-1.5">
                    <span>Access guide</span>
                    <ArrowUpRight className="size-3.5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Card 4: Case Study Retrospective (Span 7 cols) */}
          <motion.div variants={itemVariants} className="md:col-span-7" data-index={3}>
            <Card className="h-full bg-card border border-border rounded-md hover:border-foreground/40 transition-[border-color,box-shadow] duration-200 shadow-sm flex flex-col justify-between p-6 sm:p-8">
              <div>
                <CardHeader className="p-0 mb-6 flex flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-sm border-border bg-muted/60 text-foreground font-mono text-xs py-0.5 px-2.5">
                      {resourcesData[3].format}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {resourcesData[3].quarter}
                    </span>
                  </div>
                  <div className="p-2 rounded-sm bg-muted/40 border border-border/60">
                    {getIcon(resourcesData[3].iconName)}
                  </div>
                </CardHeader>

                <CardTitle className="p-0 text-2xl sm:text-3xl font-serif font-semibold leading-snug mb-4 text-foreground">
                  <Link to={resourcesData[3].to} className="hover:underline underline-offset-4 decoration-border">
                    {resourcesData[3].title}
                  </Link>
                </CardTitle>

                <CardContent className="p-0">
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {resourcesData[3].abstract}
                  </p>
                </CardContent>
              </div>

              <CardFooter className="p-0 pt-4 border-t border-border/80 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>{resourcesData[3].pagesOrReadTime}</span>
                <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-foreground font-sans font-medium hover:bg-muted/80">
                  <Link to={resourcesData[3].to} className="inline-flex items-center gap-1.5">
                    <span>Review case data</span>
                    <ArrowUpRight className="size-3.5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}